import React, { useState, useContext, useEffect, useLayoutEffect } from 'react';
import {
  AppHeader,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useHistory } from 'react-router-dom';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { numberHelper } from '../utils/numberHelper';
import { IPaperlessOptOut, Pages, PaperlessOptOutType } from '@lib/types';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { getNextPage } from '../utils/getNextPage';
import { CartProductCode, CurrentPayPlan, FlowType, MembershipLevel } from '@ace-clublabs/common-membership-api-types';
import { ProductType } from '../application/models/NewJoinSessionData';
import { RemoveWarningModal, RemoveWarningModalContentProps } from '../components/RemoveWarningModal';
import {
  isAutopayRequired,
  isAutopayBillPlan,
  isAutopayAllowed,
  isFullDigitalRequired,
  isPaperlessOptOutAllowed,
  getAutopayTextSection1,
  getAutopayTextSection2,
  getFullDigitalDisclaimer,
  historyPushState,
  getProgressProps,
} from '../utils/helpers';
import { TotalCostHeader } from '../components/TotalCostHeader';
import { LineItem } from '../components/LineItem';
import { UtagType } from '../utils/UtagHelper';
import { getRecurringPeriodLabel, stringHelper } from '../utils/stringHelper';
import { BenefitsPage } from './BenefitsPage';
import { getCartProductName } from '../utils/helpers';
import { AppProgressHeader } from '../components/AppProgressHeader';

export const ReviewPage: React.FC = () => {
  const {
    membershipInfo,
    sessionData,
    refreshSession,
    loadingInfo,
    sendTag,
    updateProducts,
    updateAssociates,
    updateBillPlan,
    isPriceCartLoading,
    getPaymentManifestId,
    updatePaperlessOption,
    updateMembershipForm,
  } = useContext(MembershipAppStateContext);
  const history = useHistory();
  const [deleteInfo, setDeleteInfo] = useState<RemoveWarningModalContentProps | null>(null);
  const [isTagging, setIsTagging] = useState(true);
  const [isChangingMembershipLevel, setIsChangingMembershipLevel] = useState(false);
  const isGiftFlow = sessionData.flowType === FlowType.GIFT;
  const giftTitle = isGiftFlow ? "Let's review your gift membership" : "Let's review!";
  const currentUrl = window.location.href;
  const urlSearchParams = new URL(currentUrl).searchParams;
  const displayLeadGeneration = sessionData.formData.mobileConsent && membershipInfo?.enableGenerateLead;

  const isChangingMembershipLevelParam = urlSearchParams.get('isChangingMembershipLevel') === 'true';

  useEffect(() => {
    setIsChangingMembershipLevel(isChangingMembershipLevelParam);
  }, [isChangingMembershipLevelParam]);

  const showBenefitPage = () => {
    urlSearchParams.set('isChangingMembershipLevel', 'true');
    const newUrl = currentUrl.split('?')[0] + '?' + urlSearchParams.toString();
    historyPushState({ path: newUrl }, '', newUrl);
    setIsChangingMembershipLevel(true);
  };

  const closeBenefitsPage = () => {
    window.history.back();
  };

  useEffect(() => {
    refreshSession();
  }, []);

  useLayoutEffect(() => {
    if (!membershipInfo?.benefits) {
      return;
    }
    const tagPage = sessionData.flowType === FlowType.GIFT ? 'membership:gift:review' : 'review';
    sendTag(UtagType.view, tagPage);
    setIsTagging(false);
  }, [membershipInfo]);

  const saveGenerateAutoLead = (generateAutoLead: boolean) => {
    const updatedFormData = {
      ...sessionData.formData,
      generateAutoLead,
    };
    updateMembershipForm(updatedFormData);
  };

  const flowType = sessionData.flowType;

  const selectedMembershipLevel = sessionData.formData.membershipLevel as MembershipLevel;

  const autoPayDiscountAmount =
    //@ts-ignore
    membershipInfo?.membershipCost?.[selectedMembershipLevel]?.annual?.autoPay?.discountAmount ?? 0;

  const showPaperlessOptOut = isPaperlessOptOutAllowed(membershipInfo?.paperlessOptOuts);
  const showAutopayEnrollment =
    !isAutopayRequired(membershipInfo!, sessionData.formData) && isAutopayAllowed(membershipInfo!);
  const showPaperlessOrAutopaySection = showAutopayEnrollment || showPaperlessOptOut;

  const showFullDigitalDisclaimer = isFullDigitalRequired(membershipInfo?.paperlessOptOuts);

  const ersFee = membershipInfo?.membershipCost?.fees?.sameDayServiceFee;

  const salesTax = sessionData?.priceCart?.salesTax || 0;

  const CartProductCodeMap = {
    [ProductType.RV]: CartProductCode.RV,
    [ProductType.ERS]: CartProductCode.ERSFEE,
  };

  const mapWarningTitleById = {
    [ProductType.RV]: 'Are you sure you want to remove roadside for RV/motorcycle?',
    [ProductType.ERS]: 'Are you sure you want to remove same day roadside service?',
  };

  const mapWarningContentById: Partial<Record<ProductType, string>> = {
    [ProductType.ERS]: `If you have a vehicle disablement before you join, that disablement will not be covered after you join as one of your four allowable service calls per membership year. If you would like such service, you may request service for an additional $${ersFee} non-refundable service fee.`,
  };

  const isMonthly = sessionData.formData.billPlan === CurrentPayPlan.RECURRINGMONTHLY;
  const showAutopay = membershipInfo === null ? false : isAutopayRequired(membershipInfo, sessionData.formData);

  const physicalCard: IPaperlessOptOut | undefined = membershipInfo?.paperlessOptOuts?.find(
    (p) => p.type === 'PHYSICAL_CARD'
  );
  const paperless: IPaperlessOptOut | undefined = membershipInfo?.paperlessOptOuts?.find(
    (p) => p.type === 'PAPERLESS_BILLING'
  );
  const magazine: IPaperlessOptOut | undefined = membershipInfo?.paperlessOptOuts?.find((p) => p.type === 'MAGAZINE');

  const promoCodeSection =
    flowType === FlowType.NEW_JOIN ? (
      <>
        <Typography variant="b2Regular" sx={{ paddingTop: '17px' }}>
          Promo code
        </Typography>

        <div className="promo-input-container">
          <TextField id="promoInput" placeholder="AAA2023" fullWidth />
          <Button id="applyButton" label="Apply" onClick={() => {}} className="apply-button buttonText" />
        </div>
      </>
    ) : (
      <></>
    );

  const getClassicBaseCost = (): string => {
    const defaultCost = 0.0;
    return numberHelper.formatMoney(membershipInfo?.membershipBaseCost?.CLASSIC?.annual?.baseCost ?? defaultCost);
  };

  const productType = getCartProductName(CartProductCode.SERVICELEVEL);

  const getTotalCost = (): string => {
    const defaultCost = 0.0;
    return numberHelper.formatMoney(sessionData?.priceCart?.total ?? defaultCost);
  };

  const isMajorRocket = flowType === FlowType.MAJOR_ROCKET;

  const serviceLevelCost = sessionData?.priceCart?.items.find((item) => item.code === CartProductCode.SERVICELEVEL);
  const admissionFeeCost = sessionData?.priceCart?.items.find((item) => item.code === CartProductCode.ADMISSION);
  const familyUpgradeCost = sessionData?.priceCart?.items.find((item) => item.code === CartProductCode.FAMILYCOST);
  const autopayDiscount = sessionData?.priceCart?.items.find((item) => item.code === CartProductCode.AUTOPAY_DISCOUNT);

  const formattedServiceLevelDiscount = Number.isInteger(serviceLevelCost?.discount)
    ? `$${serviceLevelCost?.discount?.toString() || '0'}`
    : stringHelper.formatMoney(serviceLevelCost?.discount?.toString() || '0');

  const majorRocketServiceLevel = (
    <>
      <div className="review-item-container">
        <Typography
          data-di-mask={`false`}
          className={`${productType}-description`}
          variant="b2Regular"
          sx={{ padding: '16px 0' }}
        >
          Classic member
        </Typography>
        <div className="flex-row-container">
          <>
            <Typography
              variant="b2Regular"
              className={`${productType}-full-price`}
              sx={{ color: '#9E9E9E', textDecorationLine: 'line-through', marginRight: '8px' }}
            >
              {getClassicBaseCost()}
            </Typography>

            <Typography
              variant="b2Regular"
              className={`${productType}-discounted-price`}
              sx={{ color: '#3E6B20', marginRight: '44px' }}
            >
              (Coupon redeemed)
            </Typography>
          </>
        </div>
      </div>
    </>
  );

  const progressProps = getProgressProps(sessionData.flowType!, Pages.ReviewPage);

  const serviceLevelSection =
    flowType === FlowType.MAJOR_ROCKET ? (
      <>{majorRocketServiceLevel}</>
    ) : (
      <>
        <LineItem
          {...serviceLevelCost!}
          mask
          description={`${serviceLevelCost?.description} - ${sessionData.formData.firstName} ${sessionData.formData.lastName}`}
        />
        {flowType === FlowType.T_MOBILE && (
          <Typography
            className="tmobile-year-on-us-message"
            variant="b3Regular"
            sx={{ color: '#3E6B20', marginTop: '-0.5rem', marginBottom: '1rem' }}
          >
            {`Year on Us by T-Mobile (${formattedServiceLevelDiscount} value)`}
          </Typography>
        )}
        {flowType === FlowType.EMPLOYEE && (
          <Typography
            className="employee-discount-message"
            variant="b3Regular"
            sx={{ color: '#3E6B20', marginTop: '-0.5rem', marginBottom: '1rem', display: 'block' }}
          >
            {`Employee discount (${formattedServiceLevelDiscount} value)`}
          </Typography>
        )}
        {flowType === FlowType.GROUPON && (
          <Typography
            className="groupon-redeemed-message"
            variant="b3Regular"
            sx={{ color: '#3E6B20', marginTop: '-0.5rem', marginBottom: '1rem' }}
          >
            {`Groupon offer applied`}
          </Typography>
        )}
      </>
    );

  const admissionFeeSection =
    flowType === FlowType.MAJOR_ROCKET ? (
      <>
        <hr className="overview-line"></hr>
        <div className="review-item-container">
          <Typography variant="b2Regular" sx={{ padding: '16px 0' }}>
            New member admission fee
          </Typography>
          <div className="flex-row-container">
            <div className="flex-row-container">
              <Typography
                variant="b2Regular"
                className={`${productType}-cost`}
                sx={{ color: '#3E6B20', marginRight: '44px' }}
              >
                Waived
              </Typography>
            </div>
          </div>
        </div>
      </>
    ) : (
      <LineItem {...admissionFeeCost!} />
    );

  const isLoading = loadingInfo.isLoading || membershipInfo?.benefits == null;
  if (isLoading) {
    return (
      <Container maxWidth="sm">
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={0} currentStep={loadingInfo.loadingDetails.length} />
      </Container>
    );
  }

  if (isTagging) {
    return <></>;
  }

  return isChangingMembershipLevel ? (
    <BenefitsPage onContinue={closeBenefitsPage} isUpgrading={true} />
  ) : (
    <>
      <AppProgressHeader {...progressProps} />

      <Typography variant="h5" className="review-header">
        {giftTitle}
      </Typography>
      <TotalCostHeader
        formattedCost={getTotalCost()}
        recurringPeriodLabel={getRecurringPeriodLabel(sessionData)}
        onChangeClick={showBenefitPage}
      />
      {/* {promoCodeSection} */}
      <div className="overview-container">
        <div className="overview-title">Membership overview</div>
        <hr className="overview-line"></hr>
        {/* Service Level Cost */}
        {serviceLevelSection}

        {/* Family Upgrade cost line item*/}
        {familyUpgradeCost ? <LineItem {...familyUpgradeCost!} /> : <></>}

        {/* Associate line items */}
        {sessionData.formData.associates?.map((associate) => {
          const associatePriceCart = sessionData.priceCart?.items.find((a) => a.id === associate.virtualId);
          return (
            <LineItem
              {...associatePriceCart!}
              mask
              onDelete={() =>
                setDeleteInfo({
                  title: `Are you sure you want to remove ${associate.firstName} ${associate.lastName} from your membership?`,
                  confirmRemove: () => {
                    updateAssociates('remove', associate);
                  },
                })
              }
            />
          );
        })}

        {/* Product line items */}
        {sessionData.formData.products?.map((product) => {
          const productPriceCart = sessionData.priceCart?.items.find((a) => a.code === CartProductCodeMap[product]);
          return (
            <LineItem
              {...productPriceCart!}
              onDelete={() =>
                setDeleteInfo({
                  title: mapWarningTitleById[product] ?? undefined,
                  warningContent: mapWarningContentById?.[product] ?? undefined,
                  confirmRemove: () => {
                    updateProducts('remove', ProductType[product]);
                  },
                })
              }
            />
          );
        })}

        {/* Admission cost line item*/}
        {admissionFeeSection}

        {/* Auto Pay line item */}
        {autopayDiscount ? (
          <>
            <LineItem
              id="autopay"
              code={CartProductCode.AUTOPAY_DISCOUNT}
              price={0}
              discount={autoPayDiscountAmount}
              discountedPrice={-autoPayDiscountAmount}
              description="Auto Pay discount"
            />
            {isAutopayRequired(membershipInfo, sessionData.formData) ? (
              <Typography variant="b3Regular" sx={{ color: '#3E6B20', marginTop: '-.25rem', marginBottom: '1rem' }}>
                Auto Pay enrollment is required for discount
              </Typography>
            ) : (
              <></>
            )}
          </>
        ) : (
          <></>
        )}

        {/* Sales Tax line item */}
        {salesTax > 0 ? (
          <LineItem id="sales-tax" code={CartProductCode.SALESTAX} price={salesTax} description="Sales tax" />
        ) : (
          <></>
        )}

        {salesTax || autopayDiscount ? <hr className="overview-line"></hr> : <></>}

        <div className="total-container">
          <Typography
            variant="b1Bold"
            sx={{ color: '#1C4DA6', marginLeft: '16px', fontWeight: 700 }}
            className="total-title"
          >
            Total
          </Typography>
          {isPriceCartLoading ? (
            <CircularProgress variant="indeterminate" size={25} className="total-cost-spinner" />
          ) : (
            <Typography
              variant="b1Bold"
              className="total-price"
              sx={{ color: '#1C4DA6', marginRight: '58px', fontWeight: 700 }}
            >
              {getTotalCost()}
            </Typography>
          )}
        </div>

        <div className="enrollments-signup" id="checkbox-align">
          {showPaperlessOrAutopaySection ? (
            <>
              <Paper elevation={8} variant="elevation" sx={{ padding: '0.25rem' }}>
                {showAutopayEnrollment ? (
                  <>
                    <Stack>
                      <Typography
                        id="review-autopay-signup"
                        variant="b1Bold"
                        sx={{ marginLeft: '1rem', marginTop: '1rem' }}
                      >
                        Would you like to enroll in Auto Pay?
                      </Typography>
                      <Typography variant="b3Regular" sx={{ color: 'green', marginLeft: '1rem' }}>
                        Save {numberHelper.formatMoney(autoPayDiscountAmount ?? 0)} with Auto Pay
                      </Typography>
                    </Stack>
                    <RadioGroup
                      row
                      variant="text"
                      id="autopay-select"
                      sx={{ marginLeft: '1rem' }}
                      value={isAutopayBillPlan(sessionData.formData) ? 'Yes' : 'No'}
                    >
                      <Radio
                        id="review-autopay-yes"
                        label="Yes"
                        value={`Yes`}
                        sx={{ outline: 'none' }}
                        inputProps={{
                          onClick: () => {
                            updateBillPlan(CurrentPayPlan.RECURRINGCREDITORDEBITCARD);
                          },
                        }}
                      ></Radio>
                      <Radio
                        id="review-autopay-no"
                        label="No"
                        value={`No`}
                        inputProps={{
                          onClick: () => {
                            updateBillPlan(CurrentPayPlan.ONETIME);
                          },
                        }}
                      ></Radio>
                    </RadioGroup>
                  </>
                ) : (
                  <></>
                )}
                {showPaperlessOptOut && showAutopayEnrollment ? (
                  <>
                    <br />
                    <hr className="overview-line"></hr>
                    <br />
                  </>
                ) : (
                  <></>
                )}
                {showPaperlessOptOut ? (
                  <>
                    <Stack className="paperless-options-content">
                      <Typography variant="h6Medium" sx={{ margin: '0 1rem' }}>
                        Paperless options
                      </Typography>
                      <Typography variant="b3Regular" sx={{ margin: '0 1rem' }}>
                        Opt out of receiving paper documents in the mail and access your AAA information digitally. You
                        can change your settings anytime in My Account. See{' '}
                        <Link
                          className="benefit-link paperless-terms-condition-link"
                          href={`https://www.ace.aaa.com/legal/paperless-terms-and-conditions.html`}
                          target="_blank"
                          variant="b3Regular"
                          sx={{ color: '#0156A3' }}
                        >
                          Terms and Conditions
                        </Link>
                        .
                      </Typography>
                    </Stack>
                    <Stack className="opt-out-checkboxes">
                      <Typography variant="b1Medium" sx={{ margin: '1rem 1rem 0 1rem' }}>
                        I don't want to receive
                      </Typography>
                      <Stack sx={{ marginLeft: '1rem' }} padding={0}>
                        {paperless?.allowed ? (
                          <Checkbox
                            variant="text"
                            sx={{
                              fontSize: '1rem',
                            }}
                            name="paperless"
                            value="paperless"
                            id="paperless-billing-or-documents-checkbox"
                            label="Paper billing or documents"
                            checked={sessionData.formData.paperlessBilling}
                            inputProps={{
                              onClick: () => {
                                updatePaperlessOption(
                                  PaperlessOptOutType.PAPERLESS_BILLING,
                                  !sessionData.formData.paperlessBilling
                                );
                              },
                            }}
                          ></Checkbox>
                        ) : (
                          <></>
                        )}
                        {physicalCard?.allowed ? (
                          <Checkbox
                            variant="text"
                            name="physicalCard"
                            value="physicalCard"
                            id="physical-membership-card-checkbox"
                            sx={{ marginTop: '0', marginBottom: '0' }}
                            checked={sessionData.formData.declinePhysicalCard}
                            inputProps={{
                              onClick: () => {
                                updatePaperlessOption(
                                  PaperlessOptOutType.PHYSICAL_CARD,
                                  !sessionData.formData.declinePhysicalCard
                                );
                              },
                            }}
                            label={
                              <>
                                <Typography sx={{ fontSize: '16px !important' }}>A physical membership card</Typography>
                                <Typography
                                  sx={{ fontSize: '14px !important', color: '#707070', whiteSpace: 'nowrap' }}
                                >
                                  Get your digital card on the Auto Club App
                                </Typography>
                              </>
                            }
                          ></Checkbox>
                        ) : (
                          <></>
                        )}
                        {magazine?.allowed ? (
                          <Checkbox
                            variant="text"
                            name="magazine"
                            value="magazine"
                            id="magazine-checkbox"
                            label={magazine.label}
                            sx={{ marginTop: '0' }}
                            checked={sessionData.formData.declineMagazine}
                            inputProps={{
                              onClick: () => {
                                updatePaperlessOption(
                                  PaperlessOptOutType.MAGAZINE,
                                  !sessionData.formData.declineMagazine
                                );
                              },
                            }}
                          ></Checkbox>
                        ) : (
                          <></>
                        )}
                      </Stack>
                    </Stack>
                  </>
                ) : (
                  <></>
                )}
              </Paper>
            </>
          ) : (
            <></>
          )}
        </div>

        {displayLeadGeneration && (
          <Box sx={{ backgroundColor: '#FAF8F4', borderRadius: '10px', padding: '0.25rem' }}>
            <Checkbox
              variant="text"
              name="generateAutoLead"
              className="generateAutoLead"
              id="generateLead-checkbox"
              label={
                <Typography>
                  Your AAA membership unlocks exclusive auto insurance savings — get your quote now.
                </Typography>
              }
              sx={{ margin: '0 3.5px 0 8px', fontSize: '1.5rem', alignSelf: 'start', paddingTop: '2px' }}
              fullWidth
              checked={sessionData.formData.generateAutoLead ?? false}
              onClick={(mouseEvent) => {
                const target = mouseEvent.target as HTMLInputElement;
                saveGenerateAutoLead(target.checked);
              }}
            />
          </Box>
        )}

        <div className="review-page-btn">
          <Button
            label={
              <>
                {isPriceCartLoading ? (
                  <div className="button-spinner">
                    <CircularProgress variant="indeterminate" size={20} color="inherit" />{' '}
                  </div>
                ) : (
                  <></>
                )}
                {(sessionData?.priceCart?.total || 0) > 0 ? 'Pay now' : 'Continue'}
              </>
            }
            disabled={isPriceCartLoading}
            fullWidth
            onClick={() => {
              if (isAutopayBillPlan(sessionData.formData)) {
                history.push(getNextPage(Pages.ReviewPage, sessionData.flowType));
              } else {
                getPaymentManifestId();
              }
            }}
            variant="contained"
            size="medium"
            id="nextButton"
          ></Button>
        </div>

        <Stack sx={{ gap: '1rem', marginTop: '1rem', marginBottom: '2rem' }}>
          {!isGiftFlow ? (
            <>
              <Stack className="review-terms-section" style={{ display: showAutopay ? 'block' : 'none' }}>
                <Typography variant="b3Regular" sx={{ color: '#404040' }}>
                  {getAutopayTextSection1(isMonthly)} See{' '}
                  <Link
                    className="benefit-link terms-condition-link"
                    href={`https://app.ace.aaa.com/payments/termsandconditions?variant=mmp&clubcode=${membershipInfo?.clubCode}`}
                    target="_blank"
                    variant="b3Regular"
                  >
                    Terms and Conditions
                  </Link>
                  {getAutopayTextSection2(isMonthly)}
                </Typography>
              </Stack>
            </>
          ) : (
            <></>
          )}
          {showFullDigitalDisclaimer ? (
            <>
              <Stack className="full-digital-section">
                <Typography variant="b3Regular" sx={{ color: '#404040' }}>
                  {getFullDigitalDisclaimer(membershipInfo?.clubCode ?? '')} See{' '}
                  <Link
                    className="benefit-link"
                    href="https://www.ace.aaa.com/legal/paperless-terms-and-conditions.html"
                    target="_blank"
                    variant="b3Regular"
                  >
                    Terms and Conditions
                  </Link>
                  . You can change these preferences at any time by signing in to My Account.
                </Typography>
              </Stack>
            </>
          ) : (
            <></>
          )}
        </Stack>
      </div>
      <RemoveWarningModal
        title={deleteInfo?.title ?? ''}
        warningContent={deleteInfo?.warningContent ?? ''}
        showModal={!!deleteInfo}
        closeModal={() => setDeleteInfo(null)}
        confirmRemove={() => {
          deleteInfo?.confirmRemove();
        }}
      />
    </>
  );
};
